export type CartResponse = {
  basketIds: string[]
  currency: string
  subtotal: string
  subtotalPoints: string
  promoCodeDiscount: string
  flatRatePromoDiscount: number
  total: string
  newCartItems: CartItem[]
  errorMessages: string[]
}

export type CartItem = {
  basketId: string
  invtId: string
  itemId: string
  alternateId: string
  quantity: number
  listPrice: number
  salePrice: number
  extensionGroups: {
    [key: string]: CartItemExtensionGroup
  }
}

export type CartItemExtensionGroup = {
  id: string
  extension: CartItemExtension
  sortIndex: number
}

export type CartItemExtension = {
  id: string
  name: string
  code: string
}

export type CartData = {
  currencyId: string
  totalFreight: number
  totalMerch: number
  totalMiscellaneous: number
  totalOrder: number
  shipViaId: string
  items: {
    basketId: string
    alternateId: string
    invtId: string
    itemId: string
    itemType: string
    quantity: number
    listPrice: number
    salePrice: number
    description: string
    descriptionEn: string
    urlPath: string
    thumbnailPath: string
    categoryId: string
    categoryName: string
    categoryNameEn: string
    parentCategoryId: string
    parentCategoryName: string
    allowSubscription: boolean
    images: {
      imageUrl: string
      itemId: string
      itemType: string
      sortIndex: number
      active: boolean
      user5: string
      user6: string
    }[]
    extensionGroups?: {
      [key: string]: {
        id: string
        sortIndex: number
        extension: {
          id: string
          name: string
          code: string
        }
      }
    }
    errorMessage: string
  }[]
}

export type ShippingMethod = {
  id: string
  name: string
  description: string
  price: string
  isAvailable: boolean
  isExternal: boolean
}

export default class API {

  static viewState: string = ''
  static viewStateGenerator: string = ''
  static viewStateEncrypted: string = ''

  static async updateCartQuantities({
    viewState,
    viewStateGenerator,
    viewStateEncrypted,
    cartItems,
  }: {
    viewState: string
    viewStateGenerator: string
    viewStateEncrypted: string
    cartItems: {
      basketId: string
      productId: string
      quantity: number
    }[]
  }) {

    let body = `__VIEWSTATE=${viewState}` +
      `&__VIEWSTATEGENERATOR=${viewStateGenerator}` +
      `&__VIEWSTATEENCRYPTED=${viewStateEncrypted}` +
      `&actionType=CARTUPDATE` +
      `&posting=TRUE` +
      `&tSearchString=`

    for (let i = 0; i < cartItems.length; i++) {

      const basketId = cartItems[i].basketId.toString()
      const productId = cartItems[i].productId.toString()
      const quantity = Number.parseInt(cartItems[i].quantity.toString())

      if (!basketId || !productId || isNaN(quantity)) {
        continue
      }

      body += `&basketID=${basketId}&productID=${productId}&quantity=${quantity}`
    }

    const response = await fetch(`/ajax/cart.aspx`, {
      "headers": {
        "content-type": "application/x-www-form-urlencoded",
      },
      "body": body,
      "method": "POST",
      "mode": "cors",
      "credentials": "include",
      "redirect": "follow"
    })

    if (response.status !== 200) {
      console.error(`Something went wrong updating cart quatities (response.status: ${response.status}).`)
      return
    }

    const ajaxResponse = await response.text()

    return API.processAjaxResponse(ajaxResponse)
  }

  static async deleteItemFromCart({ basketId }: { basketId: string }) {

    if (!basketId) {
      throw new Error('[deleteItemFromCart] Missing basketId')
    }

    const response = await fetch(`/ajax/cart.aspx?posting=true&actionType=CARTDELETE&basketID=${basketId}`, {
      "method": "GET",
      "mode": "cors",
      "credentials": "include",
      "redirect": "follow"
    })

    if (response.status !== 200) {
      console.error(`[deleteItemFromCart] Something went wrong deleting product from cart (response.status: ${response.status}).`)
      return
    }

    const ajaxResponse = await response.text()

    return API.processAjaxResponse(ajaxResponse)
  }

  static async addOneItemToCart({
    itemId,
    extensionsGroups = '',
    extensions = [],
    quantity = '1'
  }: {
    itemId: string
    extensionsGroups: string
    extensions: string[]
    quantity: string
  }) {

    const extensionsGroupsSplited = extensionsGroups.split(';')

    let body = `&posting=TRUE` +
      `&actionType=CARTADD` +
      `&productID=${itemId}` +
      `&quantity=${quantity}` +
      `&productIDExtensionGroups=${encodeURIComponent(extensionsGroups)}`

    for (let i = 0; i < extensions.length; i++) {
      const extensionGroup = extensionsGroupsSplited[i]
      const extensionValue = extensions[i]
      if (extensionGroup && extensionValue) {
        body += `&${itemId}${extensionsGroupsSplited[i]}=${extensions[i]}`
      }
    }

    const result = await fetch(`/ajax/cart.aspx`, {
      "headers": {
        "content-type": "application/x-www-form-urlencoded;"
      },
      "body": body,
      "method": "POST",
      "mode": "cors",
      "credentials": "include"
    })

    const ajaxResponse = await result.text()

    return API.processAjaxResponse(ajaxResponse)
  }

  static async addItemsToCart(itemsToBeAdded: {
    itemId: string
    quantity: string | number
    extensionGroups: {
      id: string
      value: string
    }[]
  }[]) {

    const itemIds: string = itemsToBeAdded.map(item => item.itemId).join(',')
    const extensionsGroups: string = itemsToBeAdded.map(item => item.extensionGroups.map(extensionGroup => extensionGroup.id).join(';')).join(',')
    const quantities: string = itemsToBeAdded.map(item => item.quantity).join(',')

    let body = `&posting=TRUE` +
      `&actionType=CARTADD` +
      `&productID=${itemIds}` +
      `&quantity=${quantities}` +
      `&productIDExtensionGroups=${encodeURIComponent(extensionsGroups)}`

    for (const itemToBeAdded of itemsToBeAdded) {
        
        const itemId = itemToBeAdded.itemId
        const extensionGroups = itemToBeAdded.extensionGroups
  
        for (const extensionGroup of extensionGroups) {
          body += `&${itemId}${extensionGroup.id}=${extensionGroup.value}`
        }
    }

    const result = await fetch(`/ajax/cart.aspx`, {
      "headers": {
        "content-type": "application/x-www-form-urlencoded;"
      },
      "body": body,
      "method": "POST",
      "mode": "cors",
      "credentials": "include"
    })

    const ajaxResponse = await result.text()

    return {
      cart: API.processAjaxResponseAddItemsToCart(ajaxResponse),
      raw: ajaxResponse
    }
  }

  static async searchItemsByKeyword({
    keywords
  }: {
    keywords: string[]
  }) {

    const body = `posting=TRUE` +
      `&actionType=doQuickSearch` +
      `&SearchString=${keywords.join(',').trim()}`

    const result = await fetch(`ajax/search.aspx`, {
      "headers": {
        "content-type": "application/x-www-form-urlencoded;"
      },
      "body": body,
      "method": "POST"
    })

    const ajaxResponse = await result.text()

    const parser = new DOMParser()

    const doc = parser.parseFromString(ajaxResponse, 'text/html')

    const results = []

    doc.querySelectorAll('rs').forEach((rsElement) => {
      results.push({
        id: rsElement.getAttribute('id'),
        invtID: rsElement.getAttribute('invtID'),
        productimage: rsElement.getAttribute('productimage'),
        producturl: rsElement.getAttribute('producturl'),
        type: rsElement.getAttribute('type'),
        qty: rsElement.getAttribute('qty'),
        stkItem: rsElement.getAttribute('stkItem'),
        keywords: rsElement.getAttribute('keywords'),
        name: rsElement.innerHTML.trim()
      })
    })

    return results
  }

  static async setShippingMethodAndGetCostsAndTotals(shipViaId: string) {

    const body = `&posting=TRUE` + 
      `&getBasket=TRUE` + 
      `&actionType=SHIPPINGSAVE` + 
      `&shipViaID=${shipViaId}`
    
    const result = await fetch(`/ajax/shipping.aspx`, {
      "headers": {
        "content-type": "application/x-www-form-urlencoded;"
      },
      "body": body,
      "method": "POST"
    })

    const ajaxResponse = await result.text()

    const parser = new DOMParser()
    const doc = parser.parseFromString(ajaxResponse, 'text/html')

    const results: { 
      subtotal: number,
      total: number,
      tax: number,
      shippingCosts: { [key: string]: number } 
    } = {
      subtotal: undefined,
      total: undefined,
      tax: undefined,
      shippingCosts: {}
    };

    const totalRows = Number((doc.querySelector('dsShipCosts t0TotalRows') as HTMLElement).innerHTML)

    for (let i = 0; i < totalRows; i++) {
      const shippingMethodId = doc.querySelector(`dsShipCosts t0r${i}shipViaID`).innerHTML
      const shippingCost = Number(doc.querySelector(`dsShipCosts t0r${i}TotalShippingCost`).innerHTML)

      results.shippingCosts[shippingMethodId] = shippingCost
    }

    const orderSubtotal = Number(doc.querySelector('dsBasket t0r0CuryTotMerch').innerHTML)
    const orderTotal = Number(doc.querySelector('dsBasket t0r0CuryTotOrd').innerHTML)
    const orderTotalTax = Number(doc.querySelector('dsBasket t0r0CuryTotTax').innerHTML)

    results.subtotal = orderSubtotal
    results.total = orderTotal
    results.tax = orderTotalTax

    return results
  }

  static async getShippingMethods() {

    const result = await fetch(`/ajax/shipping.aspx`, {
      "method": "POST",
      "mode": "cors",
      "credentials": "include"
    })

    const ajaxResponse = await result.text()

    const parser = new DOMParser()
    const doc = parser.parseFromString(ajaxResponse, 'text/html')

    const results: ShippingMethod[] = []

    const totalRows = Number((doc.querySelector('dsShipVia t0TotalRows') as HTMLElement).innerHTML)

    for (let i = 0; i < totalRows; i++) {
      const shippingMethodId = doc.querySelector(`dsShipVia t0r${i}shipViaID`).innerHTML
      const shippingMethodName = doc.querySelector(`dsShipVia t0r${i}Descr`).innerHTML
      const shippingMethodDescription = doc.querySelector(`dsShipVia t0r${i}longDescr`).innerHTML
      const shippingMethodPrice = doc.querySelector(`dsShipVia t0r${i}price`).innerHTML
      const shippingMethodIsAvailable = doc.querySelector(`dsShipVia t0r${i}available`).innerHTML === '1'
      const shippingMethodIsExternal = doc.querySelector(`dsShipVia t0r${i}externalPrice`).innerHTML === '1'

      results.push({
        id: shippingMethodId,
        name: shippingMethodName,
        description: shippingMethodDescription,
        price: shippingMethodPrice,
        isAvailable: shippingMethodIsAvailable,
        isExternal: shippingMethodIsExternal
      })
    }

    return results
  }

  static processAjaxResponse(ajaxResponse: string) {

    function extractContentBetweenTags(input: string, tagName: string) {
      const regex = new RegExp(`<${tagName}>([\\s\\S]*?)</${tagName}>`, 'i')
      const match = input.match(regex)
      return match ? match[1].trim() : null
    }

    const curyIdRegex = /<t0r0CuryID>(.*?)<\/t0r0CuryID>/ // currency id (USD) 
    const curyTotMerchRegex = /<t0r0CuryTotMerch>(.*?)<\/t0r0CuryTotMerch>/ // subtotal 
    const user6Regex = /<t0r0User6>(.*?)<\/t0r0User6>/ // subtotal (points)
    const curyTotMiscRegex = /<t0r0CuryTotMisc>(.*?)<\/t0r0CuryTotMisc>/ // discount
    const curyTotOrdRegex = /<t0r0CuryTotOrd>(.*?)<\/t0r0CuryTotOrd>/ // total

    const basketIdRegex = /<t1r\dbasketid>(.*?)<\/t1r\dbasketid>/g
    const alternateIdRegex = /<t1r\dalternateid>(.*?)<\/t1r\dalternateid>/g
    const invtIdRegex = /<t1r\dinvtid>(.*?)<\/t1r\dinvtid>/g
    const itemIdRegex = /<t1r\ditemid>(.*?)<\/t1r\ditemid>/g
    const listPriceRegex = /<t1r\dcurylistprice>(.*?)<\/t1r\dcurylistprice>/g
    const salePriceRegex = /<t1r\dcuryslsprice>(.*?)<\/t1r\dcuryslsprice>/g
    const quantitiesRegex = /<t1r\dqtyord>(.*?)<\/t1r\dqtyord>/g
    const errorMessageRegex = /<errormessage>.+<\/errormessage>/i

    const extensionsDataset = extractContentBetweenTags(ajaxResponse, 'dsBasketExtensions')
    const extensionsDatasetFirstTableTotalRow = Number.parseInt(extractContentBetweenTags(extensionsDataset, 't0TotalRows'))

    const basketIds = []
    const alternateIds = []
    const invtIds = []
    const itemIds = []
    const listPrices = []
    const salePrices = []
    const quantities = []
    const errorMessages = []

    let match

    // Extract error messages
    while ((match = errorMessageRegex.exec(ajaxResponse)) !== null) {
      errorMessages.push(match[1])
    }

    // Extract basketID values
    while ((match = basketIdRegex.exec(ajaxResponse)) !== null) {
      basketIds.push(match[1])
    }

    // Extract alternateID values
    while ((match = alternateIdRegex.exec(ajaxResponse)) !== null) {
      alternateIds.push(match[1])
    }

    // Extract invtID values
    while ((match = invtIdRegex.exec(ajaxResponse)) !== null) {
      invtIds.push(match[1])
    }

    // Extract itemID values
    while ((match = itemIdRegex.exec(ajaxResponse)) !== null) {
      itemIds.push(match[1])
    }

    // Extract list price values
    while ((match = listPriceRegex.exec(ajaxResponse)) !== null) {
      listPrices.push(match[1])
    }

    // Extract sale price values
    while ((match = salePriceRegex.exec(ajaxResponse)) !== null) {
      salePrices.push(match[1])
    }

    // Extract quantity values
    while ((match = quantitiesRegex.exec(ajaxResponse)) !== null) {
      quantities.push(match[1])
    }

    // Extract 't0r0CuryID' tag
    const curyIdMatch = curyIdRegex.exec(ajaxResponse)

    // Extract 't0r0CuryTotMerch' tag
    const curyTotMerchMatch = curyTotMerchRegex.exec(ajaxResponse)

    // Extract 't0r0User6' tag
    const user6Match = user6Regex.exec(ajaxResponse)

    // Extract 'curyTotMisc' tag
    const curyTotMiscMatch = curyTotMiscRegex.exec(ajaxResponse)

    // Extract 't0r0CuryTotOrd' tag
    const curyTotOrdMatch = curyTotOrdRegex.exec(ajaxResponse)

    const curyId = curyIdMatch[1]
    const curyTotMerch = curyTotMerchMatch[1]
    const user6 = user6Match[1]
    const curyTotOrd = curyTotOrdMatch[1]
    const curyTotMisc = curyTotMiscMatch[1]

    // console.log('[processAjaxResponse] basketIds:', basketIds)
    // console.log('[processAjaxResponse] curyId:', curyId)
    // console.log('[processAjaxResponse] curyTotMerch (subtotal):', curyTotMerch)
    // console.log('[processAjaxResponse] user6 (subtotalPoints):', user6)


    let flatRatePromoDiscount = 0

    const newCartItems: CartItem[] = []

    // Build the cartItems array
    for (let i = 0; i < basketIds.length; i++) {
      const basketId = basketIds[i]
      const alternateId = alternateIds[i]
      const invtId = invtIds[i]
      const itemId = itemIds[i]
      const quantity = quantities[i]
      const listPrice = listPrices[i]
      const salePrice = salePrices[i]

      if (!basketId || !invtId || isNaN(Number.parseInt(quantity))) {
        continue
      }

      if (!Number.isInteger(Number.parseFloat(quantity))) {
        alert(`Quantity must be an integer. Received: ${quantity}`)
        return
      }

      const newCartItem: CartItem = {
        basketId: basketId,
        invtId: invtId,
        itemId: itemId,
        alternateId: alternateId,
        quantity: Number.parseInt(quantity),
        listPrice: Number.parseFloat(listPrice),
        salePrice: Number.parseFloat(salePrice) || undefined,
        extensionGroups: {}
      }

      flatRatePromoDiscount += newCartItem.salePrice 
        ? ((newCartItem.listPrice - newCartItem.salePrice) * newCartItem.quantity) 
        : 0

      for (let i = 0; i < extensionsDatasetFirstTableTotalRow; i++) {

        const basketId = extractContentBetweenTags(extensionsDataset, `t0r${i}basketID`)
        const extensionGroupId = extractContentBetweenTags(extensionsDataset, `t0r${i}extensionGroupID`)
        const extensionId = extractContentBetweenTags(extensionsDataset, `t0r${i}extensionID`)
        const itemId = extractContentBetweenTags(extensionsDataset, `t0r${i}itemId`)
        const sortIndex = Number.parseInt(extractContentBetweenTags(extensionsDataset, `t0r${i}sortIndex`))
        const description = extractContentBetweenTags(extensionsDataset, `t0r${i}Descr`)
        const name = extractContentBetweenTags(extensionsDataset, `t0r${i}Name`)
        const nameEn = extractContentBetweenTags(extensionsDataset, `t0r${i}NameEn`)
        const errorMessage = extractContentBetweenTags(extensionsDataset, `t0r${i}errorMessage`)

        if (!newCartItem.extensionGroups[extensionGroupId]) {
          newCartItem.extensionGroups[extensionGroupId] = {
            id: extensionGroupId,
            sortIndex: sortIndex,
            extension: undefined
          }
        }

        newCartItem.extensionGroups[extensionGroupId]['extension'] = {
          id: extensionId,
          name: name,
          code: extensionId
        }
      }

      newCartItems.push(newCartItem)
    }

    const result: CartResponse = {
      basketIds: basketIds,
      currency: curyId,
      subtotal: curyTotMerch,
      subtotalPoints: user6,
      promoCodeDiscount: curyTotMisc,
      flatRatePromoDiscount: flatRatePromoDiscount,
      total: curyTotOrd,
      newCartItems: newCartItems,
      errorMessages: errorMessages
    }

    return result
  }

  static processAjaxResponseAddItemsToCart(ajaxResponse: string) {

    function extractContentBetweenTags(input: string, tagName: string) {
      const regex = new RegExp(`<${tagName}>([\\s\\S]*?)</${tagName}>`, 'i')
      const match = input.match(regex)
      return match ? match[1].trim() : null
    }

    const dsBasketContent = extractContentBetweenTags(ajaxResponse, 'dsBasket')
    const currencyId = extractContentBetweenTags(dsBasketContent, `t0r0CuryID`)
    const totalFreight = Number(extractContentBetweenTags(dsBasketContent, `t0r0CuryTotFrt`))
    const totalMerch = Number(extractContentBetweenTags(dsBasketContent, `t0r0CuryTotMerch`))
    const totalMiscellaneous = Number(extractContentBetweenTags(dsBasketContent, `t0r0CuryTotMisc`))
    const totalOrder = Number(extractContentBetweenTags(dsBasketContent, `t0r0CuryTotOrd`))
    const shipViaId = extractContentBetweenTags(dsBasketContent, `t0r0ShipViaID`)

    const cart: CartData = {
      currencyId: currencyId,
      totalFreight: totalFreight,
      totalMerch: totalMerch,
      totalMiscellaneous: totalMiscellaneous,
      totalOrder: totalOrder,
      shipViaId: shipViaId,
      items: []
    }

    const dsBasketT1TotalRows = Number.parseInt(extractContentBetweenTags(dsBasketContent, `t1TotalRows`))

    for (let i = 0; i < dsBasketT1TotalRows; i++) {

      const basketId = extractContentBetweenTags(dsBasketContent, `t1r${i}basketid`)
      const alternateId = extractContentBetweenTags(dsBasketContent, `t1r${i}alternateid`)
      const invtId = extractContentBetweenTags(dsBasketContent, `t1r${i}invtid`)
      const itemId = extractContentBetweenTags(dsBasketContent, `t1r${i}itemid`)
      const itemType = extractContentBetweenTags(dsBasketContent, `t1r${i}itemtype`)
      const quantity = Number(extractContentBetweenTags(dsBasketContent, `t1r${i}qtyOrd`))
      const listPrice = Number(extractContentBetweenTags(dsBasketContent, `t1r${i}curylistprice`))
      const salePrice = Number(extractContentBetweenTags(dsBasketContent, `t1r${i}curyslsprice`))
      const description = extractContentBetweenTags(dsBasketContent, `t1r${i}descr`)
      const descriptionEn = extractContentBetweenTags(dsBasketContent, `t1r${i}descr_en`)
      const urlPath = extractContentBetweenTags(dsBasketContent, `t1r${i}cartuser1`)
      const thumbnailPath = extractContentBetweenTags(dsBasketContent, `t1r${i}thumbnail`)
      const categoryId = extractContentBetweenTags(dsBasketContent, `t1r${i}categoryid`)
      const categoryName = extractContentBetweenTags(dsBasketContent, `t1r${i}categoryname`)
      const categoryNameEn = extractContentBetweenTags(dsBasketContent, `t1r${i}categoryname_en`)
      const parentCategoryId = extractContentBetweenTags(dsBasketContent, `t1r${i}parentcategoryid`)
      const parentCategoryName = extractContentBetweenTags(dsBasketContent, `t1r${i}parentcategoryname`)
      const allowSubscription = extractContentBetweenTags(dsBasketContent, `t1r${i}allowsubscription`) === '1'
      const errorMessage = extractContentBetweenTags(dsBasketContent, `t1r${i}errorMessage`) || undefined

      cart.items.push({
        basketId: basketId,
        alternateId: alternateId,
        invtId: invtId,
        itemId: itemId,
        itemType: itemType,
        quantity: quantity,
        listPrice: listPrice,
        salePrice: salePrice,
        description: description,
        descriptionEn: descriptionEn,
        urlPath: urlPath,
        thumbnailPath: thumbnailPath,
        categoryId: categoryId,
        categoryName: categoryName,
        categoryNameEn: categoryNameEn,
        parentCategoryId: parentCategoryId,
        parentCategoryName: parentCategoryName,
        allowSubscription: allowSubscription,
        images: [],
        errorMessage: errorMessage
      })
    }

    const dsBasketExtensionsContent = extractContentBetweenTags(ajaxResponse, 'dsBasketExtensions')
    const dsBasketExtensionsT0TotalRows = Number.parseInt(extractContentBetweenTags(dsBasketExtensionsContent, `t0TotalRows`))

    for (let i = 0; i < dsBasketExtensionsT0TotalRows; i++) {

      const basketId = extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}basketID`)
      const extensionGroupId = extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}extensionGroupID`)
      const extensionId = extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}extensionID`)
      // const itemId = extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}itemId`)
      const sortIndex = Number.parseInt(extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}sortIndex`))
      // const description = extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}Descr`)
      // const name = extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}Name`)
      // const nameEn = extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}name_en`)
      const errorMessage = extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}errorMessage`) || undefined

      const extensionGroup = {
        id: extensionGroupId,
        errorMessage: errorMessage,
        sortIndex: sortIndex,
        extension: {
          id: extensionId,
          name: extractContentBetweenTags(dsBasketExtensionsContent, `t0r${i}Name`),
          code: extensionId,
        }
      }

      const item = cart.items.find(item => item.basketId === basketId)

      if (!item) {
        continue
      }

      if (!item.extensionGroups) {
        item.extensionGroups = {}
      }

      item.extensionGroups[extensionGroupId] = extensionGroup
    }

    const dsBasketImagesContent = extractContentBetweenTags(ajaxResponse, 'dsBasketImages')
    const dsBasketImagesT0TotalRows = Number.parseInt(extractContentBetweenTags(dsBasketImagesContent, `t0TotalRows`))

    for (let i = 0; i < dsBasketImagesT0TotalRows; i++) {

      const basketId = extractContentBetweenTags(dsBasketImagesContent, `t0r${i}basketID`)
      const imageUrl = extractContentBetweenTags(dsBasketImagesContent, `t0r${i}imageURL`)
      const itemId = extractContentBetweenTags(dsBasketImagesContent, `t0r${i}itemID`)
      const itemType = extractContentBetweenTags(dsBasketImagesContent, `t0r${i}itemType`)
      const sortIndex = Number.parseInt(extractContentBetweenTags(dsBasketImagesContent, `t0r${i}sortIndex`))
      const active = extractContentBetweenTags(dsBasketImagesContent, `t0r${i}active`) === '1'
      const user5 = extractContentBetweenTags(dsBasketImagesContent, `t0r${i}User5`)
      const user6 = extractContentBetweenTags(dsBasketImagesContent, `t0r${i}User6`)

      const image = {
        imageUrl: imageUrl,
        itemId: itemId,
        itemType: itemType,
        sortIndex: sortIndex,
        active: active,
        user5: user5,
        user6: user6
      }

      const item = cart.items.find(item => item.basketId === basketId)

      if (!item) {
        continue
      }

      item.images.push(image)
    }

    return cart
  }
}